import { type JSX, type ReactNode, useId, useState } from 'react';

import { versandname, type Nachrichtzeile, type Versandstand, type Versandzeile } from '../api/support';
import { alsZeitpunkt } from '../datum';
import { markenklasse, type Markenart } from '../marken';

/**
 * DER KUNDENVERKEHR ALS KARTEN (Auftrag CRM-196; SUP-04, SUP-15, SUP-19).
 *
 * Wunsch von Ulf (11.10.2026): „wenn ein Vorgang offen ist, ist die Trennung
 * zwischen den einzelnen Emailverläufen nicht klar erkennbar …"
 *
 * Jede Nachricht ist eine abgesetzte Karte, älteste zuerst (SUP-04). Die
 * Kopfzeile nennt Richtung (Zeichen und Wort), Absender und Zeitpunkt, bei
 * einer Antwort den Stand im Postausgang (SUP-19). Ausgehende Karten sind
 * eingerückt und tragen eine farbige Kante — erkennbar auch ohne Farbe.
 *
 * Ab drei Nachrichten ist nur die neueste offen; die älteren zeigen Kopf und
 * Textanfang und lassen sich einzeln oder alle zusammen aufklappen. Der Fuß
 * (Abtrennen, SUP-15) steht an jeder Karte, auch zugeklappt.
 */

/** Ab so vielen Nachrichten klappen die älteren zu. */
export const ZUKLAPPEN_AB = 3;

const VERSANDMARKE: Readonly<Record<Versandstand, Markenart>> = {
  bereit: 'laufend',
  laeuft: 'laufend',
  uebernommen: 'abschluss',
  aufgegeben: 'sackgasse',
};

const ANFANG_ZEICHEN = 160;

/** Der Textanfang einer zugeklappten Karte — nur Text, nie Markup. */
export function textanfang(n: Pick<Nachrichtzeile, 'textRein' | 'textHtml'>): string {
  let text = n.textRein ?? '';
  if (text.trim() === '' && n.textHtml !== null && typeof DOMParser !== 'undefined') {
    // DOMParser führt nichts aus; Stil und Skript fallen vorher heraus, damit ihr Inhalt nicht als Text erscheint.
    const dok = new DOMParser().parseFromString(n.textHtml, 'text/html');
    for (const weg of dok.querySelectorAll('style, script')) weg.remove();
    text = dok.body.textContent;
  }
  const flach = text.replace(/\s+/g, ' ').trim();
  return flach.length > ANFANG_ZEICHEN ? `${flach.slice(0, ANFANG_ZEICHEN).trimEnd()} …` : flach;
}

/** Älteste zuerst; gleiche Zeit behält die Reihenfolge des Servers. */
function chronologisch(nachrichten: readonly Nachrichtzeile[]): Nachrichtzeile[] {
  return nachrichten
    .map((n, i) => ({ n, i, t: Date.parse(n.eingegangenAm) }))
    .sort((a, b) => (a.t === b.t || Number.isNaN(a.t) || Number.isNaN(b.t) ? a.i - b.i : a.t - b.t))
    .map((e) => e.n);
}

function zaehlung(nachrichten: readonly Nachrichtzeile[]): string {
  const ein = nachrichten.filter((n) => n.richtung === 'eingehend').length;
  const aus = nachrichten.length - ein;
  const gesamt = nachrichten.length === 1 ? '1 Nachricht' : `${String(nachrichten.length)} Nachrichten`;
  return `${gesamt} · ${String(ein)} eingehend, ${String(aus)} ausgehend`;
}

export function Nachrichtenverlauf({
  nachrichten,
  versand,
  nachricht,
  fuss,
}: {
  readonly nachrichten: readonly Nachrichtzeile[];
  /** SUP-19: je ausgehender Nachricht der Stand im Postausgang; fehlt, solange die Bearbeitung lädt. */
  readonly versand: Readonly<Record<string, Versandzeile>> | undefined;
  /** Die Darstellung einer Nachricht (Kopf, Text, Anhänge). */
  readonly nachricht: (n: Nachrichtzeile) => ReactNode;
  /** Was unter jeder Karte steht, auch zugeklappt — Abtrennen (SUP-15). */
  readonly fuss?: ((n: Nachrichtzeile) => ReactNode) | undefined;
}): JSX.Element {
  // Ausdrücklich auf- oder zugeklappt; alles andere folgt der Vorgabe. So bleibt eine neue Nachricht offen.
  const [auf, setzeAuf] = useState<ReadonlySet<string>>(new Set());
  const [zu, setzeZu] = useState<ReadonlySet<string>>(new Set());

  const reihe = chronologisch(nachrichten);
  const klappbar = reihe.length >= ZUKLAPPEN_AB;
  const neueste = reihe.at(-1)?.id;
  const offen = (id: string): boolean => {
    if (!klappbar) return true;
    if (zu.has(id)) return false;
    return auf.has(id) || id === neueste;
  };
  const alleOffen = reihe.every((n) => offen(n.id));

  const umschalten = (id: string): void => {
    const jetzt = offen(id);
    setzeAuf((s) => mit(s, id, !jetzt));
    setzeZu((s) => mit(s, id, jetzt));
  };

  return (
    <div>
      <div className="nachrichtverlauf__kopf">
        <p className="nachrichtverlauf__zahl">{zaehlung(reihe)}</p>
        {klappbar && (
          <button
            type="button"
            className="knopf knopf--klein"
            data-auch-telefon
            onClick={() => {
              setzeAuf(alleOffen ? new Set() : new Set(reihe.map((n) => n.id)));
              setzeZu(new Set());
            }}
          >
            {alleOffen ? 'Ältere zuklappen' : 'Alle aufklappen'}
          </button>
        )}
      </div>
      <ol className="nachrichtverlauf__liste">
        {reihe.map((n, i) => (
          <li key={n.id}>
            <Nachrichtenkarte
              nachricht={n}
              nummer={i + 1}
              gesamt={reihe.length}
              versand={n.richtung === 'ausgehend' ? versand?.[n.id] : undefined}
              klappbar={klappbar}
              offen={offen(n.id)}
              beiUmschalten={() => umschalten(n.id)}
              inhalt={nachricht}
              fuss={fuss?.(n)}
            />
          </li>
        ))}
      </ol>
    </div>
  );
}

function mit(s: ReadonlySet<string>, id: string, drin: boolean): ReadonlySet<string> {
  const neu = new Set(s);
  if (drin) neu.add(id);
  else neu.delete(id);
  return neu;
}

function Nachrichtenkarte({
  nachricht: n,
  nummer,
  gesamt,
  versand,
  klappbar,
  offen,
  beiUmschalten,
  inhalt,
  fuss,
}: {
  readonly nachricht: Nachrichtzeile;
  readonly nummer: number;
  readonly gesamt: number;
  readonly versand: Versandzeile | undefined;
  readonly klappbar: boolean;
  readonly offen: boolean;
  readonly beiUmschalten: () => void;
  readonly inhalt: (n: Nachrichtzeile) => ReactNode;
  readonly fuss: ReactNode;
}): JSX.Element {
  const koerper = useId();
  const aus = n.richtung === 'ausgehend';
  const wer = n.absendername !== null && n.absendername.trim() !== '' ? n.absendername : n.absender;
  const wann = alsZeitpunkt(n.eingegangenAm) ?? n.eingegangenAm;

  const kopf = (
    <>
      <span className="nachrichtkarte__richtung">
        <span aria-hidden="true">{aus ? '→' : '←'}</span> {aus ? 'Ausgehend von uns' : 'Eingehend vom Kunden'}
      </span>
      <span className="nachrichtkarte__wer">{wer}</span>
      <span className="nachrichtkarte__wann">
        <time dateTime={n.eingegangenAm}>{wann}</time>
        <span className="hilfe"> · {nummer} von {gesamt}</span>
      </span>
    </>
  );

  return (
    <article
      className={`nachrichtkarte nachrichtkarte--${aus ? 'ausgehend' : 'eingehend'}${offen ? '' : ' nachrichtkarte--zu'}`}
      aria-label={`${aus ? 'Ausgehende' : 'Eingehende'} Nachricht ${String(nummer)} von ${String(gesamt)}`}
    >
      <header className="nachrichtkarte__kopf">
        {klappbar ? (
          <button
            type="button"
            className="nachrichtkarte__schalter"
            data-auch-telefon
            aria-expanded={offen}
            aria-controls={koerper}
            onClick={beiUmschalten}
          >
            <span className="nachrichtkarte__pfeil" aria-hidden="true">{offen ? '▾' : '▸'}</span>
            {kopf}
          </button>
        ) : (
          <div className="nachrichtkarte__zeile">{kopf}</div>
        )}
        {/* SUP-19: Stand im Postausgang, auch zugeklappt. */}
        {versand !== undefined && (
          <span className={markenklasse(VERSANDMARKE[versand.stand])}>{versandname(versand)}</span>
        )}
      </header>
      {versand !== undefined && versand.fehler !== null && <p className="feld__fehler nachrichtkarte__fehler">{versand.fehler}</p>}
      <div id={koerper} className="nachrichtkarte__koerper">
        {offen ? inhalt(n) : <p className="nachrichtkarte__anfang">{textanfang(n) || '(ohne Text)'}</p>}
      </div>
      {fuss !== undefined && fuss !== null && fuss !== false && <div className="nachrichtkarte__fuss">{fuss}</div>}
    </article>
  );
}
